import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, NgZone } from '@angular/core';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, delay, map, switchMap } from 'rxjs/operators';
import { ApiService } from '../../core/services/api.service';
import { environment } from '../../../environments/environment';
import { COMMON_CONSTANTS } from '../../shared/constants/common.constants';
import { PAYLOAD_ENCRYPTION } from '../../shared/constants/payload-encryption.constants';
import { shouldEncryptForUrl } from '../../shared/config/payload-encryption.config';
import { PayloadCryptoService } from '../../shared/services/payload-crypto.service';
import { ToastService } from '../../shared/services/toast.service';
import { ClientIpService } from '../../shared/services/client-ip.service';
import { UserService } from '../../shared/services/user.service';
import { redirectToAtsAfterUnauthorized } from '../../shared/utils/ats-unauthorized-redirect';
import {
  isRateLimitHttpStatus,
  parseFetchErrorPayload,
  parseRetryAfterMs,
} from '../../shared/utils/http-error.util';
import {
  parseAgentBffChatSseErrorData,
  type AgentBffChatSseErrorPayload,
  type AgentBffChatSseResult,
  type AgenticAiConversation,
  type AgenticAiConversationListResponse,
  type AgenticAiConversationSearchHit,
  type FavouritePromptsListResponse,
  type ApplicantPreview,
  type AgenticAiMessage,
  normalizeMessageFeedbackRating,
  normalizeMessageIsFavourite,
  normalizeAgenticMessageRole,
  type AgentSseProgressEvent,
  type CreateAgenticConversationDto,
  type GetMessagesQuery,
  type PaginatedMessagesResponse,
  type PostUserMessageDto,
  type PostUserMessageResponse,
} from './agentic-ai.models';
import type { PostConversationMessageBody } from './bq-pagination';
import {
  normalizeAssignableUser,
  normalizeCompanyTokenSummary,
  normalizeTokenAllocationActivity,
  normalizeUserQuotaUsageMetrics,
  normalizeUsageSummary,
  normalizeUserTokenQuota,
  type AssignableUserOption,
  type AddUserTokenQuotaTokensDto,
  type CompanyTokenAllocationSummary,
  type CreateUserTokenQuotaDto,
  type ListTokenAllocationActivitiesQuery,
  type ListUserTokenQuotasQuery,
  type TokenAllocationActivityListResponse,
  type UpdateUserTokenQuotaDto,
  type UsageSummary,
  type UserQuotaUsageMetrics,
  type UserQuotaUsagePeriod,
  type UserQuotaRenewalType,
  type UserTokenQuota,
  type UserTokenQuotaListResponse,
} from './token-allocation.models';

/** Handlers for `GET …/agent/stream` — Pub/Sub workflow events forwarded by the BFF. */
export interface AgentBffWorkflowStreamHandlers {
  /**
   * `eventName` is the SSE `event:` field (e.g. `workflow.task.complete`).
   * `data` is the parsed JSON body (the upstream `ProgressEvent`).
   */
  onWorkflow?: (args: { eventName: string; data: Record<string, unknown> }) => void;
  onHeartbeat?: () => void;
  /** Invoked when the HTTP stream closes normally (reconnect if needed). */
  onStreamEnd?: () => void;
  /** Invoked when the server returns HTTP 429 — caller should back off reconnects. */
  onRateLimited?: (retryAfterMs: number) => void;
}

export interface AgentChatRequest {
  prompt: string;
  /**
   * Domain Orchestrator conversation id. Omit for **new** threads — UI calls `POST …/agent/chat` (no id) via **SSE**.
   * BFF must stream SSE and return `conversationId` on the `result` event.
   */
  conversationId?: string;
  /** Optional (e.g. `title` / `initialPrompt` for new chat, or `bqPageRequest` for paging). */
  metadata?: Record<string, unknown> | null;
  /** Client public IP (best-effort); persisted on user message rows. */
  ipAddress?: string;
}

/** Relative to apiUrl (see environment.apiUrl — should end with /api). Example: agentic-ai */
const DEFAULT_PREFIX = 'agentic-ai';

/**
 * BFF `POST …/agent/chat` and `POST …/agent/chat/:conversationId` must respond with **Server-Sent Events**
 * (`Content-Type: text/event-stream`). Implemented via `fetch` in `postAgentChatStream`, not `HttpClient`.
 */
const BFF_AGENT_CHAT_SSE_ACCEPT = 'text/event-stream';

@Injectable({ providedIn: 'root' })
export class AgenticAiApiService {
  private readonly http = inject(HttpClient);
  private readonly api = inject(ApiService);
  private readonly payloadCrypto = inject(PayloadCryptoService);
  private readonly ngZone = inject(NgZone);
  private readonly user = inject(UserService);
  private readonly toast = inject(ToastService);
  private readonly clientIp = inject(ClientIpService);

  /** `fetch` + SSE run outside Angular's zone; wrap so templates update on each `progress` event. */
  private async resolveClientIpForUserMessage(explicit?: string): Promise<string | undefined> {
    const fromArg = explicit?.trim();
    if (fromArg) {
      return fromArg;
    }
    const resolved = await this.clientIp.resolve();
    return resolved?.trim() || undefined;
  }

  private emitSse<T>(fn: () => T): T {
    return this.ngZone.run(fn);
  }

  private get prefix(): string {
    const p = environment.agenticAiApiPrefix ?? DEFAULT_PREFIX;
    return p.replace(/^\/+|\/+$/g, '');
  }

  private path(segment: string): string {
    const s = segment.startsWith('/') ? segment.slice(1) : segment;
    return `/${this.prefix}/${s}`;
  }

  /** Encrypted public conversation id for URL path segments. */
  private encPublicIdSegment(id: string): string {
    return encodeURIComponent(String(id).trim());
  }

  private useMock(): boolean {
    return environment.agenticAiUseMock;
  }

  /**
   * POST `{BFFOrchApiUrl}/agent/chat` (new thread) or `…/agent/chat/:conversationId` (follow-up) — SSE.
   * Events: `progress`, `result` (include `conversationId` for new threads), `error`.
   */
  async postAgentChatStream(
    body: AgentChatRequest,
    handlers: {
      onProgress: (e: AgentSseProgressEvent) => void;
      onResult: (res: AgentBffChatSseResult) => void;
      onError: (err: AgentBffChatSseErrorPayload) => void;
    },
    signal?: AbortSignal,
  ): Promise<void> {
    const base = environment.BFFOrchApiUrl.replace(/\/$/, '');
    const id =
      body.conversationId != null && String(body.conversationId).trim()
        ? String(body.conversationId).trim()
        : '';
    const hasConversationId = id.length > 0;
    const url = hasConversationId
      ? `${base}/agent/chat/${encodeURIComponent(id)}`
      : `${base}/agent/chat`;

    if (this.useMock()) {
      this.emitSse(() => handlers.onProgress({ phase: 'preparing' }));
      await new Promise((r) => setTimeout(r, 60));
      this.emitSse(() => handlers.onProgress({ phase: 'routing', message: 'Validating session' }));
      await new Promise((r) => setTimeout(r, 80));
      this.emitSse(() => handlers.onProgress({ phase: 'running_query', message: 'Calling orchestrator' }));
      await new Promise((r) => setTimeout(r, 80));
      const mockConvId = hasConversationId ? id : 'mock-conv-1';
      this.emitSse(() =>
        handlers.onResult({
          conversationId: mockConvId,
          reply: 'Mock BFF reply. Connect the live BFF SSE endpoint for real responses.',
        }),
      );
      return;
    }

    const ipAddress = await this.resolveClientIpForUserMessage(body.ipAddress);
    const jsonBody: Record<string, unknown> = { prompt: body.prompt };
    if (body.metadata != null) {
      jsonBody['metadata'] = body.metadata;
    }
    if (ipAddress) {
      jsonBody['ipAddress'] = ipAddress;
    }
    let requestBody: BodyInit;
    const headers: Record<string, string> = {
      Accept: BFF_AGENT_CHAT_SSE_ACCEPT,
      'Cache-Control': 'no-cache',
    };
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem(COMMON_CONSTANTS.JWT_TOKEN_KEY) : null;
    if (token) {
      headers['Authorization'] = `${COMMON_CONSTANTS.JWT_TOKEN_PREFIX} ${token}`;
    }

    if (shouldEncryptForUrl(url) && this.payloadCrypto.isConfigured()) {
      const envelope = await this.payloadCrypto.encryptUtf8(JSON.stringify(jsonBody));
      requestBody = JSON.stringify({
        [PAYLOAD_ENCRYPTION.BODY_KEY]: envelope,
      });
      headers['Content-Type'] = 'application/json';
      headers[PAYLOAD_ENCRYPTION.HEADER_ENCRYPTED] = '1';
      headers[PAYLOAD_ENCRYPTION.HEADER_VERSION] = String(PAYLOAD_ENCRYPTION.VERSION);
    } else {
      requestBody = JSON.stringify(jsonBody);
      headers['Content-Type'] = 'application/json';
    }

    const res = await fetch(url, {
      method: 'POST',
      headers,
      body: requestBody,
      signal,
    });
    if (!res.ok || !res.body) {
      if (res.status === 401) {
        redirectToAtsAfterUnauthorized(this.user, this.toast);
        return;
      }
      let bodyText = '';
      try {
        bodyText = (await res.text()).trim();
      } catch {
        /* ignore */
      }
      if (isRateLimitHttpStatus(res.status)) {
        const parsed = parseFetchErrorPayload(bodyText, res.status);
        this.toast.warn(parsed.message);
      }
      const parsed = parseFetchErrorPayload(bodyText, res.status);
      const payload =
        bodyText.trim().length > 0
          ? bodyText
          : JSON.stringify({
              message: parsed.message,
              ...(parsed.code ? { code: parsed.code } : {}),
            });
      this.emitSse(() => handlers.onError(parseAgentBffChatSseErrorData(payload, res.status)));
      return;
    }

    const sseCheck = await this.assertBffChatResponseIsSse(res);
    if (!sseCheck.ok) {
      this.emitSse(() =>
        handlers.onError(
          parseAgentBffChatSseErrorData(JSON.stringify({ message: sseCheck.message }), res.status),
        ),
      );
      return;
    }

    for await (const ev of this.readSseEvents(res.body, signal)) {
      if (ev.event === 'progress') {
        try {
          const data = JSON.parse(ev.data.trim()) as AgentSseProgressEvent;
          const hasPhase = data?.phase != null && String(data.phase).length > 0;
          const hasMsg = data?.message != null && String(data.message).trim().length > 0;
          if (hasPhase || hasMsg) {
            this.emitSse(() => handlers.onProgress(data));
          }
        } catch {
          /* ignore */
        }
        continue;
      }
      if (ev.event === 'result') {
        try {
          const raw = JSON.parse(ev.data.trim()) as AgentBffChatSseResult;
          this.emitSse(() => handlers.onResult(raw));
        } catch (e) {
          this.emitSse(() =>
            handlers.onError(
              parseAgentBffChatSseErrorData(
                JSON.stringify({ message: e instanceof Error ? e.message : 'Invalid result payload' }),
                res.status,
              ),
            ),
          );
        }
        return;
      }
      if (ev.event === 'error') {
        try {
          const errPayload = parseAgentBffChatSseErrorData(ev.data.trim(), res.status);
          this.emitSse(() => handlers.onError(errPayload));
        } catch {
          this.emitSse(() =>
            handlers.onError(parseAgentBffChatSseErrorData(ev.data || 'Stream error', res.status)),
          );
        }
        return;
      }
    }

    this.emitSse(() =>
      handlers.onError(
        parseAgentBffChatSseErrorData(JSON.stringify({ message: 'Stream ended without result' }), res.status),
      ),
    );
  }

  /**
   * GET `{BFFOrchApiUrl}/agent/stream` — long-lived SSE for workflow / task-graph Pub/Sub events.
   * Resolves when the stream ends or `signal` aborts. Ignores `heartbeat` unless `onHeartbeat` is set.
   */
  async connectBffWorkflowStream(
    handlers: AgentBffWorkflowStreamHandlers,
    signal?: AbortSignal,
  ): Promise<void> {
    if (this.useMock()) {
      return;
    }

    const base = environment.BFFOrchApiUrl.replace(/\/$/, '');
    const url = `${base}/agent/stream`;
    const headers: Record<string, string> = {
      Accept: BFF_AGENT_CHAT_SSE_ACCEPT,
      'Cache-Control': 'no-cache',
    };
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem(COMMON_CONSTANTS.JWT_TOKEN_KEY) : null;
    if (token) {
      headers['Authorization'] = `${COMMON_CONSTANTS.JWT_TOKEN_PREFIX} ${token}`;
    }

    let res: Response;
    try {
      res = await fetch(url, {
        method: 'GET',
        headers,
        signal,
      });
    } catch {
      return;
    }

    if (!res.ok || !res.body) {
      if (res.status === 401) {
        redirectToAtsAfterUnauthorized(this.user, this.toast);
      } else if (isRateLimitHttpStatus(res.status)) {
        let bodyText = '';
        try {
          bodyText = (await res.text()).trim();
        } catch {
          /* ignore */
        }
        const parsed = parseFetchErrorPayload(bodyText, res.status);
        this.toast.warn(parsed.message);
        const retryAfterMs = parseRetryAfterMs(res.headers);
        if (handlers.onRateLimited) {
          this.emitSse(() => handlers.onRateLimited!(retryAfterMs));
        }
      }
      return;
    }

    const sseCheck = await this.assertBffChatResponseIsSse(res);
    if (!sseCheck.ok) {
      return;
    }

    try {
      for await (const ev of this.readSseEvents(res.body, signal)) {
        if (ev.event === 'heartbeat') {
          if (handlers.onHeartbeat) {
            this.emitSse(() => handlers.onHeartbeat!());
          }
          continue;
        }
        const trimmed = ev.data.trim();
        if (!trimmed) {
          continue;
        }
        try {
          const data = JSON.parse(trimmed) as Record<string, unknown>;
          if (handlers.onWorkflow) {
            this.emitSse(() => handlers.onWorkflow!({ eventName: ev.event, data }));
          }
        } catch {
          /* ignore non-JSON frames */
        }
      }
    } finally {
      this.emitSse(() => handlers.onStreamEnd?.());
    }
  }

  /**
   * Staging/prod BFF must return SSE for agent chat. If the server answers with JSON (wrong handler),
   * fail fast with a clear message instead of "Stream ended without result".
   */
  private async assertBffChatResponseIsSse(res: Response): Promise<{ ok: true } | { ok: false; message: string }> {
    const ct = res.headers.get('Content-Type') ?? '';
    if (/event-stream/i.test(ct)) {
      return { ok: true };
    }
    const cl = res.headers.get('Content-Length');
    const maxProbe = 8192;
    const smallEnough = cl == null || (Number(cl) >= 0 && Number(cl) <= maxProbe);
    if (!smallEnough) {
      return { ok: true };
    }
    if (!ct.includes('application/json')) {
      return { ok: true };
    }
    try {
      const text = await res.clone().text();
      const trimmed = text.trim();
      if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
        return { ok: true };
      }
      const j = JSON.parse(trimmed) as { message?: string };
      const serverMsg = typeof j?.message === 'string' && j.message.trim() ? `${j.message.trim()} — ` : '';
      return {
        ok: false,
        message: `${serverMsg}BFF must return Server-Sent Events for POST /api/agent/chat (Content-Type: text/event-stream), not JSON. Same as POST /api/agent/chat/:conversationId.`,
      };
    } catch {
      return { ok: true };
    }
  }

  /**
   * GET `…/conversations` with optional `limit` / `offset` query params.
   * Backend may ignore params and return the full list.
   */
  listConversations(options?: { limit?: number; offset?: number }): Observable<AgenticAiConversationListResponse> {
    if (this.useMock()) {
      return of(
        mockConversationList(options?.offset ?? 0, options?.limit ?? 5),
      ).pipe(delay(200));
    }
    const params: Record<string, string | number | boolean> = {};
    if (options?.limit != null) {
      params['limit'] = options.limit;
    }
    if (options?.offset != null) {
      params['offset'] = options.offset;
    }
    return this.api
      .get<{
        items?: AgenticAiConversation[];
        total?: number;
        hasMore?: boolean;
      }>(this.path('conversations'), Object.keys(params).length ? params : undefined)
      .pipe(
        map((res) => ({
          items: (res.items ?? []).map((c) => this.normalizeConversation(c)),
          total: res.total,
          hasMore: res.hasMore,
        })),
      );
  }

  /**
   * Search conversation titles (Domain Orch).
   *
   * Domain Orch `GET …/conversations?q=…&limit=…` — title search on `agentic_ai_conversations`.
   * Falls back to client-side filtering only if the server does not support `q`.
   */
  searchConversationsByTitle(
    query: string,
    options?: { limit?: number },
  ): Observable<AgenticAiConversationSearchHit[]> {
    const q = query.trim();
    if (!q) {
      return of([]);
    }
    const limit = options?.limit ?? 25;
    const qLower = q.toLowerCase();

    if (this.useMock()) {
      return this.listConversations({ limit: 200, offset: 0 }).pipe(
        map((res) =>
          (res.items ?? [])
            .filter((c) => (c.title ?? '').toLowerCase().includes(qLower))
            .slice(0, limit)
            .map((c) => this.toSearchHit(c)),
        ),
      );
    }

    return this.api
      .get<{
        items?: Record<string, unknown>[];
        total?: number;
        hasMore?: boolean;
      }>(this.path('conversations'), { q, limit })
      .pipe(
        map((res) => (res.items ?? []).map((c) => this.normalizeSearchHit(c))),
        catchError(() =>
          this.listConversations({ limit: 200, offset: 0 }).pipe(
            map((res) =>
              (res.items ?? [])
                .filter((c) => (c.title ?? '').toLowerCase().includes(qLower))
                .slice(0, limit)
                .map((c) => this.toSearchHit(c)),
            ),
          ),
        ),
      );
  }

  createConversation(body: CreateAgenticConversationDto = {}): Observable<AgenticAiConversation> {
    if (this.useMock()) {
      const c: AgenticAiConversation = {
        id: mockNextId(),
        title: body.title ?? 'New chat',
        status: 'active',
        metadata: body.metadata ?? null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      mockStoreConversation(c);
      return of(c).pipe(delay(200));
    }
    return this.api
      .post<AgenticAiConversation>(this.path('conversations'), body)
      .pipe(map((c) => this.normalizeConversation(c)));
  }

  getConversation(conversationId: string): Observable<AgenticAiConversation> {
    if (this.useMock()) {
      const c = mockGetConversation(conversationId);
      if (!c) {
        return throwError(
          () => new HttpErrorResponse({ status: 404, statusText: 'Not Found' }),
        );
      }
      return of(c).pipe(delay(150));
    }
    return this.api
      .get<AgenticAiConversation>(this.path(`conversations/${this.encPublicIdSegment(conversationId)}`))
      .pipe(map((c) => this.normalizeConversation(c)));
  }

  getMessages(conversationId: string, query: GetMessagesQuery): Observable<PaginatedMessagesResponse> {
    if (this.useMock()) {
      return of(mockMessagesPage(conversationId, query)).pipe(delay(200));
    }
    const params: Record<string, string | number | boolean> = { limit: query.limit };
    if (query.cursor) {
      const n = Number(query.cursor);
      if (Number.isFinite(n)) {
        params['beforeSequence'] = n;
      }
    }
    return this.api
      .get<{ messages?: unknown[] }>(
        this.path(`conversations/${this.encPublicIdSegment(conversationId)}/messages`),
        params,
      )
      .pipe(
        map((res) => {
          const page = this.mapNestMessagesPage(res, query.limit);
          return {
            ...page,
            items: page.items.map((m) => ({
              ...m,
              conversationId: m.conversationId?.trim() || conversationId,
            })),
          };
        }),
      );
  }

  postUserMessage(
    conversationId: string,
    body: PostUserMessageDto,
  ): Observable<PostUserMessageResponse> {
    if (this.useMock()) {
      if (!mockConversations.has(conversationId)) {
        return throwError(
          () => new HttpErrorResponse({ status: 404, statusText: 'Not Found' }),
        );
      }
      return of(mockPostMessage(conversationId, body)).pipe(delay(400));
    }
    return from(this.resolveClientIpForUserMessage(body.ipAddress)).pipe(
      switchMap((ipAddress) =>
        this.api.post<unknown>(this.path(`conversations/${conversationId}/messages`), {
          content: body.content,
          role: 'user' as const,
          metadata: body.metadata,
          ...(ipAddress ? { ipAddress } : {}),
        }),
      ),
      map((raw) => this.mapNestPostMessageResponse(raw)),
    );
  }

  /**
   * POST `/api/agentic-ai/conversations/:id/messages/stream` — SSE progress then final `result`
   * (same persistence as {@link postUserMessage}). Uses `fetch` + Authorization header.
   */
  async postUserMessageStream(
    conversationId: string,
    body: PostUserMessageDto,
    handlers: {
      onProgress: (e: AgentSseProgressEvent) => void;
      onResult: (res: PostUserMessageResponse) => void;
      onError: (err: AgentBffChatSseErrorPayload) => void;
    },
    signal?: AbortSignal,
  ): Promise<void> {
    if (this.useMock()) {
      this.emitSse(() => handlers.onProgress({ phase: 'preparing' }));
      await new Promise((r) => setTimeout(r, 80));
      this.emitSse(() => handlers.onProgress({ phase: 'routing' }));
      await new Promise((r) => setTimeout(r, 120));
      if (!mockConversations.has(conversationId)) {
        this.emitSse(() =>
          handlers.onError(parseAgentBffChatSseErrorData(JSON.stringify({ message: 'Not Found' }))),
        );
        return;
      }
      this.emitSse(() => handlers.onResult(mockPostMessage(conversationId, body)));
      return;
    }

    const url = this.api.buildUrl(this.path(`conversations/${conversationId}/messages/stream`));
    const ipAddress = await this.resolveClientIpForUserMessage(body.ipAddress);
    const jsonBody: Record<string, unknown> = {
      content: body.content,
      role: 'user',
    };
    if (body.metadata != null) {
      jsonBody['metadata'] = body.metadata;
    }
    if (ipAddress) {
      jsonBody['ipAddress'] = ipAddress;
    }

    let requestBody: BodyInit;
    const headers: Record<string, string> = {
      Accept: 'text/event-stream',
    };
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem(COMMON_CONSTANTS.JWT_TOKEN_KEY) : null;
    if (token) {
      headers['Authorization'] = `${COMMON_CONSTANTS.JWT_TOKEN_PREFIX} ${token}`;
    }

    if (shouldEncryptForUrl(url) && this.payloadCrypto.isConfigured()) {
      const envelope = await this.payloadCrypto.encryptUtf8(JSON.stringify(jsonBody));
      requestBody = JSON.stringify({
        [PAYLOAD_ENCRYPTION.BODY_KEY]: envelope,
      });
      headers['Content-Type'] = 'application/json';
      headers[PAYLOAD_ENCRYPTION.HEADER_ENCRYPTED] = '1';
      headers[PAYLOAD_ENCRYPTION.HEADER_VERSION] = String(PAYLOAD_ENCRYPTION.VERSION);
    } else {
      requestBody = JSON.stringify(jsonBody);
      headers['Content-Type'] = 'application/json';
    }

    const res = await fetch(url, {
      method: 'POST',
      headers,
      body: requestBody,
      signal,
    });
    if (!res.ok || !res.body) {
      if (res.status === 401) {
        redirectToAtsAfterUnauthorized(this.user, this.toast);
        return;
      }
      let bodyText = '';
      try {
        bodyText = (await res.text()).trim();
      } catch {
        /* ignore */
      }
      const parsed = parseFetchErrorPayload(bodyText, res.status);
      if (isRateLimitHttpStatus(res.status)) {
        this.toast.warn(parsed.message);
      }
      this.emitSse(() =>
        handlers.onError(
          parseAgentBffChatSseErrorData(
            JSON.stringify({
              message: parsed.message,
              ...(parsed.code ? { code: parsed.code } : {}),
            }),
            res.status,
          ),
        ),
      );
      return;
    }

    for await (const ev of this.readSseEvents(res.body, signal)) {
      if (ev.event === 'progress') {
        try {
          const data = JSON.parse(ev.data.trim()) as AgentSseProgressEvent;
          const hasPhase = data?.phase != null && String(data.phase).length > 0;
          const hasMsg = data?.message != null && String(data.message).trim().length > 0;
          if (hasPhase || hasMsg) {
            this.emitSse(() => handlers.onProgress(data));
          }
        } catch {
          /* ignore malformed progress */
        }
        continue;
      }
      if (ev.event === 'result') {
        try {
          const raw = JSON.parse(ev.data.trim()) as unknown;
          this.emitSse(() => handlers.onResult(this.mapNestPostMessageResponse(raw)));
        } catch (e) {
          this.emitSse(() =>
            handlers.onError(
              parseAgentBffChatSseErrorData(
                JSON.stringify({ message: e instanceof Error ? e.message : 'Invalid result payload' }),
                res.status,
              ),
            ),
          );
        }
        return;
      }
      if (ev.event === 'error') {
        try {
          const errPayload = parseAgentBffChatSseErrorData(ev.data.trim(), res.status);
          this.emitSse(() => handlers.onError(errPayload));
        } catch {
          this.emitSse(() =>
            handlers.onError(parseAgentBffChatSseErrorData(ev.data || 'Stream error', res.status)),
          );
        }
        return;
      }
    }

    this.emitSse(() =>
      handlers.onError(
        parseAgentBffChatSseErrorData(JSON.stringify({ message: 'Stream ended without result' }), res.status),
      ),
    );
  }

  private async *readSseEvents(
    stream: ReadableStream<Uint8Array>,
    signal?: AbortSignal,
  ): AsyncGenerator<{ event: string; data: string }> {
    const reader = stream.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    try {
      while (true) {
        if (signal?.aborted) {
          await reader.cancel();
          return;
        }
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        // HTTP often uses CRLF; splitting only on \n\n misses \r\n\r\n so no events yield until close.
        buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        let sep: number;
        while ((sep = buffer.indexOf('\n\n')) >= 0) {
          const raw = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          const parsed = this.parseSseBlock(raw);
          if (parsed) {
            yield parsed;
          }
        }
      }
      const tail = buffer.trim();
      if (tail) {
        const parsed = this.parseSseBlock(tail);
        if (parsed) {
          yield parsed;
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  private parseSseBlock(raw: string): { event: string; data: string } | null {
    let event = 'message';
    const dataLines: string[] = [];
    for (const line of raw.split('\n')) {
      if (line.startsWith('event:')) {
        event = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trimStart());
      }
    }
    const data = dataLines.join('\n');
    if (!data.trim() && event === 'message') {
      return null;
    }
    return { event, data };
  }

  /**
   * POST `/api/agentic-ai/conversations/:id/messages` — use for BQ pagination payloads
   * (`content` + optional `role` + `metadata.bqPageRequest`).
   */
  postConversationMessage(
    conversationId: string,
    payload: PostConversationMessageBody,
  ): Observable<PostUserMessageResponse> {
    if (this.useMock()) {
      return this.postUserMessage(conversationId, {
        content: payload.content,
        metadata: payload.metadata ?? undefined,
      });
    }
    return from(this.resolveClientIpForUserMessage(payload.ipAddress)).pipe(
      switchMap((ipAddress) =>
        this.api.post<unknown>(this.path(`conversations/${conversationId}/messages`), {
          content: payload.content,
          role: payload.role ?? 'user',
          metadata: payload.metadata,
          ...(ipAddress ? { ipAddress } : {}),
        }),
      ),
      map((raw) => this.mapNestPostMessageResponse(raw)),
    );
  }

  /**
   * BigQuery pagination: POST `{BFFOrchApiUrl}/agent/chat/:conversationId` (JSON).
   * BFF runs SQL rules scan, then forwards to Domain Orchestrator `…/agentic-ai/conversations/:id/messages`.
   */
  postBffAgentChatBqPagination(
    conversationId: string,
    payload: PostConversationMessageBody,
  ): Observable<PostUserMessageResponse> {
    if (this.useMock()) {
      return this.postUserMessage(conversationId, {
        content: payload.content,
        metadata: payload.metadata ?? undefined,
      });
    }
    const base = environment.BFFOrchApiUrl.replace(/\/$/, '');
    const url = `${base}/agent/chat/${encodeURIComponent(String(conversationId).trim())}`;
    return from(this.resolveClientIpForUserMessage(payload.ipAddress)).pipe(
      switchMap((ipAddress) => {
        const plain: Record<string, unknown> = { prompt: payload.content };
        if (payload.metadata != null) {
          plain['metadata'] = payload.metadata;
        }
        if (ipAddress) {
          plain['ipAddress'] = ipAddress;
        }
        return this.buildBffJsonBody(url, plain);
      }),
      switchMap(({ finalUrl, body, headers }) => this.http.post<unknown>(finalUrl, body, { headers })),
      map((raw) => this.mapNestPostMessageResponse(raw)),
    );
  }

  /** Shared JSON body for BFF POST (optional AES envelope when enabled). */
  private async buildBffJsonBody(
    url: string,
    plain: Record<string, unknown>,
  ): Promise<{ finalUrl: string; body: unknown; headers: Record<string, string> }> {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem(COMMON_CONSTANTS.JWT_TOKEN_KEY) : null;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
    if (token) {
      headers['Authorization'] = `${COMMON_CONSTANTS.JWT_TOKEN_PREFIX} ${token}`;
    }
    if (shouldEncryptForUrl(url) && this.payloadCrypto.isConfigured()) {
      const envelope = await this.payloadCrypto.encryptUtf8(JSON.stringify(plain));
      return {
        finalUrl: url,
        body: { [PAYLOAD_ENCRYPTION.BODY_KEY]: envelope },
        headers: {
          ...headers,
          [PAYLOAD_ENCRYPTION.HEADER_ENCRYPTED]: '1',
          [PAYLOAD_ENCRYPTION.HEADER_VERSION]: String(PAYLOAD_ENCRYPTION.VERSION),
        },
      };
    }
    return { finalUrl: url, body: plain, headers };
  }

  deleteConversation(conversationId: string): Observable<{ success?: boolean; message?: string }> {
    return this.api.delete<{ success?: boolean; message?: string }>(
      this.path(`conversations/${this.encPublicIdSegment(conversationId)}`),
    );
  }

  listFavouritePrompts(options?: {
    limit?: number;
    offset?: number;
  }): Observable<FavouritePromptsListResponse> {
    if (this.useMock()) {
      return of({ items: [], hasMore: false }).pipe(delay(150));
    }
    const params: Record<string, string | number> = {};
    if (options?.limit != null) {
      params['limit'] = options.limit;
    }
    if (options?.offset != null) {
      params['offset'] = options.offset;
    }
    return this.api
      .get<{ items?: unknown[]; hasMore?: boolean }>(this.path('prompts/favourites'), params)
      .pipe(
        map((res) => ({
          items: (res.items ?? []).map((m) => this.normalizeServerMessage(m)),
          hasMore: res.hasMore === true,
        })),
      );
  }

  getApplicantPreview(encryptedId: string): Observable<ApplicantPreview> {
    const id = String(encryptedId ?? '').trim();
    if (!id) {
      return throwError(() => new Error('encryptedId is required'));
    }
    return this.api.get<Record<string, unknown>>('/applicants/preview', { encryptedId: id }).pipe(
      map((res) => this.normalizeApplicantPreview(res)),
    );
  }

  private normalizeApplicantPreview(raw: Record<string, unknown>): ApplicantPreview {
    const resumeContent =
      typeof raw['resumeContent'] === 'string'
        ? raw['resumeContent']
        : typeof raw['resume_content'] === 'string'
          ? raw['resume_content']
          : null;
    return {
      ...(raw as unknown as ApplicantPreview),
      encryptedId: String(raw['encryptedId'] ?? raw['encrypted_id'] ?? ''),
      resumeContent,
    };
  }

  /** @deprecated Use {@link getApplicantPreview}. */
  getApplicantBasicInfo(encryptedId: string): Observable<ApplicantPreview> {
    return this.getApplicantPreview(encryptedId);
  }

  setMessageFavourite(
    conversationId: string,
    messageId: string,
    isFavourite: boolean,
  ): Observable<AgenticAiMessage> {
    const value = isFavourite ? 1 : 0;
    return this.api
      .patch<AgenticAiMessage>(
        this.path(
          `conversations/${this.encPublicIdSegment(conversationId)}/messages/${this.encPublicIdSegment(messageId)}`,
        ),
        { isFavourite: value },
      )
      .pipe(map((m) => this.normalizeServerMessage(m)));
  }

  setMessageFeedback(
    conversationId: string,
    messageId: string,
    feedbackRating: 0 | 1 | 2,
    feedbackComment?: string | null,
  ): Observable<AgenticAiMessage> {
    const body: { feedbackRating: 0 | 1 | 2; feedbackComment?: string } = {
      feedbackRating,
    };
    if (feedbackRating === 2) {
      const comment = feedbackComment?.trim() ?? '';
      if (comment) {
        body.feedbackComment = comment;
      }
    }
    return this.api
      .patch<AgenticAiMessage>(
        this.path(
          `conversations/${this.encPublicIdSegment(conversationId)}/messages/${this.encPublicIdSegment(messageId)}/feedback`,
        ),
        body,
      )
      .pipe(map((m) => this.normalizeServerMessage(m)));
  }

  pinConversation(conversationId: string, pinned: boolean): Observable<AgenticAiConversation> {
    const pinnedValue = pinned ? 1 : 0;
    return this.api
      .patch<AgenticAiConversation>(this.path(`conversations/${this.encPublicIdSegment(conversationId)}`), {
        pinned: pinnedValue,
      })
      .pipe(
        map((c) => this.normalizeConversation(c)),
        catchError(() =>
          this.api
            .post<AgenticAiConversation>(this.path(`conversations/${this.encPublicIdSegment(conversationId)}/pin`), {
              pinned: pinnedValue,
            })
            .pipe(map((c) => this.normalizeConversation(c))),
        ),
      );
  }

  /** Partial update (title / metadata merge / pin). */
  patchConversation(
    conversationId: string,
    body: { title?: string | null; metadata?: Record<string, unknown>; pinned?: 0 | 1 },
  ): Observable<AgenticAiConversation> {
    if (this.useMock()) {
      const c = mockGetConversation(conversationId);
      if (!c) {
        return throwError(
          () => new HttpErrorResponse({ status: 404, statusText: 'Not Found' }),
        );
      }
      const next: AgenticAiConversation = {
        ...c,
        ...(body.title !== undefined ? { title: body.title } : {}),
        ...(body.pinned !== undefined ? { pinned: body.pinned } : {}),
        metadata:
          body.metadata != null
            ? { ...(c.metadata ?? {}), ...body.metadata }
            : c.metadata ?? null,
        updatedAt: new Date().toISOString(),
      };
      mockStoreConversation(next);
      return of(next).pipe(delay(150));
    }
    return this.api
      .patch<AgenticAiConversation>(
        this.path(`conversations/${this.encPublicIdSegment(conversationId)}`),
        body,
      )
      .pipe(map((c) => this.normalizeConversation(c)));
  }

  private toSearchHit(c: AgenticAiConversation): AgenticAiConversationSearchHit {
    return {
      id: c.id,
      title: c.title ?? null,
      createdAt: c.createdAt,
      updatedAt: c.updatedAt,
    };
  }

  private normalizeSearchHit(raw: Record<string, unknown>): AgenticAiConversationSearchHit {
    const idRaw = raw['id'];
    const id =
      typeof idRaw === 'string' && idRaw.trim()
        ? idRaw.trim()
        : idRaw != null
          ? String(idRaw).trim()
          : '';
    return {
      id,
      title: (raw['title'] as string | null) ?? null,
      createdAt: raw['createdAt'] != null ? String(raw['createdAt']) : undefined,
      updatedAt: raw['updatedAt'] != null ? String(raw['updatedAt']) : undefined,
    };
  }

  private normalizeConversation(c: AgenticAiConversation | Record<string, unknown>): AgenticAiConversation {
    const o = c as Record<string, unknown>;
    const status = o['status'];
    const idRaw = o['id'];
    const id =
      typeof idRaw === 'string' && idRaw.trim()
        ? idRaw.trim()
        : idRaw != null
          ? String(idRaw).trim()
          : '';
    return {
      id,
      title: (o['title'] as string | null) ?? null,
      status: typeof status === 'number' ? String(status) : String(status ?? ''),
      pinned: Number(o['pinned'] ?? 0) === 1 ? 1 : 0,
      pinnedOn:
        o['pinnedOn'] != null
          ? String(o['pinnedOn'])
          : o['pinned_on'] != null
            ? String(o['pinned_on'])
            : null,
      metadata: (o['metadata'] as Record<string, unknown> | null) ?? null,
      createdAt: o['createdAt'] != null ? String(o['createdAt']) : undefined,
      updatedAt: o['updatedAt'] != null ? String(o['updatedAt']) : undefined,
    };
  }

  private mapNestMessagesPage(res: { messages?: unknown[] }, limit: number): PaginatedMessagesResponse {
    const raw =
      (Array.isArray(res.messages) ? res.messages : null) ??
      (res != null &&
      typeof res === 'object' &&
      !Array.isArray(res) &&
      (res as Record<string, unknown>)['data'] != null &&
      typeof (res as Record<string, unknown>)['data'] === 'object' &&
      !Array.isArray((res as Record<string, unknown>)['data'])
        ? ((res as Record<string, unknown>)['data'] as Record<string, unknown>)['messages']
        : null) ??
      [];
    const items = (Array.isArray(raw) ? raw : [])
      .map((m) => this.normalizeServerMessage(m))
      .sort((a, b) => a.sequence - b.sequence);
    const hasMore = items.length === limit && items.length > 0;
    const minSeq = items.length ? Math.min(...items.map((i) => i.sequence)) : 0;
    const nextCursor = hasMore ? String(minSeq) : null;
    return { items, nextCursor, hasMore };
  }

  /**
   * BFF / Nest may return `{ userMessage, assistantMessage, bqPageInPlace }`, snake_case, or
   * wrapped in `data` / `result`. Missing `bqPageInPlace` breaks in-place UI merges.
   */
  private mapNestPostMessageResponse(raw: unknown): PostUserMessageResponse {
    const top = this.unwrapAgentChatPostEnvelope(raw);
    if (top && typeof top === 'object' && !Array.isArray(top)) {
      const r = top as Record<string, unknown>;
      const u = r['userMessage'] ?? r['user_message'];
      const a = r['assistantMessage'] ?? r['assistant_message'];
      if (u != null && a != null) {
        return {
          userMessage: this.normalizeServerMessage(u),
          assistantMessage: this.normalizeServerMessage(a),
          bqPageInPlace: this.isTruthyClientFlag(r['bqPageInPlace'] ?? r['bq_page_in_place']),
        };
      }
    }
    const userMessage = this.normalizeServerMessage(raw);
    const assistantMessage: AgenticAiMessage = {
      ...userMessage,
      id: undefined,
      role: 'assistant',
      sequence: userMessage.sequence + 1,
      content:
        'User message was saved. Wire your LLM in Nest to persist assistant replies here.',
      model: null,
      metadata: { placeholder: true },
    };
    return { userMessage, assistantMessage };
  }

  /** BFF JSON: unwrap `data` / `result` when messages live one level down. */
  private unwrapAgentChatPostEnvelope(raw: unknown): unknown {
    if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
      return raw;
    }
    const o = raw as Record<string, unknown>;
    const tryInner = (inner: unknown): unknown => {
      if (inner == null || typeof inner !== 'object' || Array.isArray(inner)) {
        return null;
      }
      const x = inner as Record<string, unknown>;
      if (
        'userMessage' in x ||
        'assistantMessage' in x ||
        'user_message' in x ||
        'assistant_message' in x
      ) {
        return inner;
      }
      return null;
    };
    const fromData = tryInner(o['data']);
    if (fromData) {
      return fromData;
    }
    const fromResult = tryInner(o['result']);
    if (fromResult) {
      return fromResult;
    }
    return raw;
  }

  private isTruthyClientFlag(v: unknown): boolean {
    if (v === true || v === 1) {
      return true;
    }
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      return s === '1' || s === 'true' || s === 'yes';
    }
    return false;
  }

  private normalizeServerMessage(raw: unknown): AgenticAiMessage {
    const o = (raw != null && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    const idRaw = o['id'] ?? o['message_id'] ?? o['messageId'];
    const id =
      idRaw != null && String(idRaw).trim() ? String(idRaw).trim() : undefined;
    const cidRaw = o['conversationId'] ?? o['conversation_id'];
    const conversationId =
      cidRaw != null && String(cidRaw).trim() ? String(cidRaw).trim() : '';
    const pt = o['promptTokens'] ?? o['prompt_tokens'];
    const ct = o['completionTokens'] ?? o['completion_tokens'];
    const tokens =
      pt != null || ct != null
        ? Number(pt ?? 0) + Number(ct ?? 0)
        : o['tokens'] != null
          ? Number(o['tokens'])
          : null;
    return {
      id,
      conversationId,
      sequence: Number(o['sequence']),
      role: normalizeAgenticMessageRole(o['role']),
      content: String(o['content'] ?? ''),
      model: o['model'] != null ? String(o['model']) : null,
      tokens,
      metadata: this.normalizeMessageMetadata(o['metadata']),
      createdAt: o['createdAt'] != null ? String(o['createdAt']) : o['created_at'] != null ? String(o['created_at']) : undefined,
      isFavourite: normalizeMessageIsFavourite(o['isFavourite'] ?? o['is_favourite']),
      feedbackRating: normalizeMessageFeedbackRating(
        o['feedbackRating'] ?? o['feedback_rating'],
      ),
      feedbackComment: (() => {
        const raw = o['feedbackComment'] ?? o['feedback_comment'];
        return raw != null && String(raw).trim() ? String(raw).trim() : null;
      })(),
    };
  }

  /**
   * GET `.../conversations/:id/messages` often returns `metadata` as a JSON string or with snake_case
   * keys; normalize so BQ UI (table vs single-record, pager) matches POST responses.
   */
  private normalizeMessageMetadata(raw: unknown): Record<string, unknown> | null {
    if (raw == null || raw === '') {
      return null;
    }
    let parsed: unknown = raw;
    if (typeof raw === 'string') {
      const s = raw.trim();
      if (!s) {
        return null;
      }
      try {
        parsed = JSON.parse(s) as unknown;
      } catch {
        return null;
      }
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return null;
    }
    const m = parsed as Record<string, unknown>;
    const out: Record<string, unknown> = { ...m };

    const copySnake = (camel: string, snake: string) => {
      if (out[camel] === undefined && out[snake] !== undefined) {
        out[camel] = out[snake];
      }
    };
    copySnake('hasMore', 'has_more');
    copySnake('totalCount', 'total_count');
    copySnake('pageSize', 'page_size');
    copySnake('page', 'page');
    copySnake('confirmation', 'confirmation_text');
    copySnake('responseMessage', 'response_message');
    if (out['confirmation'] == null && typeof out['result'] === 'object' && out['result'] != null) {
      const r = out['result'] as Record<string, unknown>;
      const c = r['confirmation'];
      if (typeof c === 'string' && c.trim() !== '') {
        out['confirmation'] = c.trim();
      }
    }

    const coerceBool = (v: unknown): boolean | undefined => {
      if (typeof v === 'boolean') {
        return v;
      }
      if (typeof v === 'number') {
        return v !== 0;
      }
      if (typeof v === 'string') {
        const x = v.trim().toLowerCase();
        if (x === 'true' || x === '1') {
          return true;
        }
        if (x === 'false' || x === '0') {
          return false;
        }
      }
      return undefined;
    };

    const hm = coerceBool(out['hasMore']);
    if (hm !== undefined) {
      out['hasMore'] = hm;
    }

    const tryParseJsonArray = (key: string) => {
      const v = out[key];
      if (typeof v === 'string') {
        try {
          const a = JSON.parse(v) as unknown;
          if (Array.isArray(a)) {
            out[key] = a;
          }
        } catch {
          /* ignore */
        }
      }
    };
    tryParseJsonArray('rows');
    tryParseJsonArray('columns');
    tryParseJsonArray('suggestions');
    tryParseJsonArray('hyperlink_columns');
    tryParseJsonArray('messages');
    tryParseJsonArray('new_submissions');
    tryParseJsonArray('existing_submissions');

    return out;
  }

  getCompanyTokenAllocationSummary(): Observable<CompanyTokenAllocationSummary> {
    if (this.useMock()) {
      return of({ allocatedTokens: 1_000_000, usedTokens: 125_000, remainingTokens: 875_000 });
    }
    return this.api
      .get<Record<string, unknown>>(this.path('token-allocation/company-summary'))
      .pipe(map((res) => normalizeCompanyTokenSummary(res)));
  }

  listUserTokenQuotas(query: ListUserTokenQuotasQuery = {}): Observable<UserTokenQuotaListResponse> {
    if (this.useMock()) {
      const mockAudit = {
        allocationDate: '2026-06-25T09:00:00.000Z',
        renewalType: 2 as UserQuotaRenewalType,
        renewalOn: 1,
        renewalStatus: 1 as const,
        renewalTokens: 50_000,
        createdAt: '2026-06-20T10:30:00.000Z',
        createdBy: 'Admin User (admin@example.com)',
        updatedAt: '2026-06-25T14:00:00.000Z',
        updatedOn: '2026-06-25T14:00:00.000Z',
      };
      const items: UserTokenQuota[] = [
        {
          id: 'mock-quota-enc-1',
          userName: 'Jane Recruiter',
          userEmail: 'jane@example.com',
          allocatedTokens: 50_000,
          usedTokens: 12_000,
          remainingTokens: 38_000,
          unauthorizedPromptAttempts: 0,
          ...mockAudit,
        },
      ];
      return of({ items, total: items.length, page: 1, limit: 20, hasMore: false });
    }
    const params: Record<string, string | number> = {};
    if (query.page != null) params['page'] = query.page;
    if (query.limit != null) params['limit'] = query.limit;
    if (query.q?.trim()) params['q'] = query.q.trim();
    if (query.remaining) params['remaining'] = query.remaining;
    if (query.sortBy) params['sortBy'] = query.sortBy;
    if (query.sortDir) params['sortDir'] = query.sortDir;
    if (query.status) params['status'] = query.status;
    return this.api.get<Record<string, unknown>>(this.path('token-allocation/user-quotas'), params).pipe(
      map((res) => {
        const rawItems = Array.isArray(res['items']) ? (res['items'] as Record<string, unknown>[]) : [];
        return {
          items: rawItems.map((row) => normalizeUserTokenQuota(row)),
          total: Number(res['total'] ?? rawItems.length) || 0,
          page: Number(res['page'] ?? query.page ?? 1) || 1,
          limit: Number(res['limit'] ?? query.limit ?? 20) || 20,
          hasMore: Boolean(res['hasMore'] ?? res['has_more']),
        };
      }),
    );
  }

  listAssignableUsers(q?: string): Observable<AssignableUserOption[]> {
    if (this.useMock()) {
      return of([
        {
          id: 'mock-user-enc-1',
          userName: 'Jane Recruiter',
          userEmail: 'jane@example.com',
        },
      ]);
    }
    const params: Record<string, string | number> = { limit: 500 };
    if (q?.trim()) {
      params['q'] = q.trim();
    }
    return this.api
      .get<{ items?: Record<string, unknown>[] }>(this.path('token-allocation/assignable-users'), params)
      .pipe(
        map((res) =>
          (Array.isArray(res.items) ? res.items : []).map((row) => normalizeAssignableUser(row)),
        ),
      );
  }

  createUserTokenQuota(body: CreateUserTokenQuotaDto): Observable<UserTokenQuota> {
    if (this.useMock()) {
      return of({
        id: 'mock-quota-enc-99',
        userName: 'Assigned User',
        userEmail: '',
        allocatedTokens: body.allocatedTokens,
        usedTokens: 0,
        remainingTokens: body.allocatedTokens,
        renewalTokens: body.allocatedTokens,
        unauthorizedPromptAttempts: 0,
        allocationDate: new Date().toISOString(),
        renewalType: body.renewalType,
        renewalOn: body.renewalOn ?? null,
        renewalStatus: body.renewalStatus,
        createdAt: new Date().toISOString(),
        createdBy: 'Admin User (admin@example.com)',
        updatedAt: new Date().toISOString(),
        updatedOn: new Date().toISOString(),
      });
    }
    return this.api
      .post<Record<string, unknown>>(this.path('token-allocation/user-quotas'), body)
      .pipe(map((res) => normalizeUserTokenQuota(res)));
  }

  updateUserTokenQuota(id: string, body: UpdateUserTokenQuotaDto): Observable<UserTokenQuota> {
    if (this.useMock()) {
      const now = new Date().toISOString();
      return of({
        id,
        userName: 'Jane Recruiter',
        userEmail: 'jane@example.com',
        allocatedTokens: body.allocatedTokens,
        usedTokens: 12_000,
        remainingTokens: Math.max(0, body.allocatedTokens - 12_000),
        renewalTokens: body.renewalTokens,
        unauthorizedPromptAttempts: 0,
        allocationDate: now,
        renewalType: body.renewalType,
        renewalOn: body.renewalOn ?? null,
        renewalStatus: body.renewalStatus,
        createdAt: '2026-06-20T10:30:00.000Z',
        createdBy: 'Admin User (admin@example.com)',
        updatedAt: now,
        updatedOn: now,
      });
    }
    const encId = encodeURIComponent(String(id).trim());
    return this.api
      .patch<Record<string, unknown>>(this.path(`token-allocation/user-quotas/${encId}`), body)
      .pipe(map((res) => normalizeUserTokenQuota(res)));
  }

  addUserTokenQuotaTokens(id: string, body: AddUserTokenQuotaTokensDto): Observable<UserTokenQuota> {
    if (this.useMock()) {
      const now = new Date().toISOString();
      const addTokens = Math.max(0, Math.floor(Number(body.addTokens) || 0));
      const allocatedTokens = 50_000 + addTokens;
      return of({
        id,
        userName: 'Jane Recruiter',
        userEmail: 'jane@example.com',
        allocatedTokens,
        usedTokens: 12_000,
        remainingTokens: Math.max(0, allocatedTokens - 12_000),
        renewalTokens: 50_000,
        unauthorizedPromptAttempts: 0,
        allocationDate: now,
        renewalType: 2 as UserQuotaRenewalType,
        renewalOn: 1,
        renewalStatus: 1 as const,
        createdAt: '2026-06-20T10:30:00.000Z',
        createdBy: 'Admin User (admin@example.com)',
        updatedAt: now,
        updatedOn: now,
      });
    }
    const encId = encodeURIComponent(String(id).trim());
    return this.api
      .patch<Record<string, unknown>>(this.path(`token-allocation/user-quotas/${encId}/add-tokens`), body)
      .pipe(map((res) => normalizeUserTokenQuota(res)));
  }

  deleteUserTokenQuota(id: string): Observable<{ deleted: true }> {
    if (this.useMock()) {
      return of({ deleted: true as const });
    }
    const encId = encodeURIComponent(String(id).trim());
    return this.api.delete<{ deleted: true }>(this.path(`token-allocation/user-quotas/${encId}`));
  }

  restoreUserTokenQuota(id: string): Observable<UserTokenQuota> {
    if (this.useMock()) {
      const now = new Date().toISOString();
      return of({
        id,
        userName: 'Restored User',
        userEmail: '',
        allocatedTokens: 10_000,
        usedTokens: 0,
        remainingTokens: 10_000,
        renewalTokens: 10_000,
        unauthorizedPromptAttempts: 0,
        isDelete: 0,
        allocationDate: now,
        renewalType: 0 as UserQuotaRenewalType,
        renewalOn: null,
        renewalStatus: 1 as const,
        createdAt: '2026-06-20T10:30:00.000Z',
        createdBy: 'Admin User (admin@example.com)',
        updatedAt: now,
        updatedOn: now,
      });
    }
    const encId = encodeURIComponent(String(id).trim());
    return this.api
      .patch<Record<string, unknown>>(this.path(`token-allocation/user-quotas/${encId}/restore`), {})
      .pipe(map((res) => normalizeUserTokenQuota(res)));
  }

  getUserQuotaUsageMetrics(
    quotaId: string,
    period: UserQuotaUsagePeriod = 'this_month',
  ): Observable<UserQuotaUsageMetrics> {
    if (this.useMock()) {
      return of({
        dataSource: 'company_database' as const,
        period,
        dateFrom: new Date(Date.now() - 30 * 86400000).toISOString(),
        dateTo: new Date().toISOString(),
        totalTokens: 67_500,
        byAgent: [
          { agentCode: 'ask_anything', agentName: 'Ask Anything', tokens: 22_000, percent: 32.6 },
          { agentCode: 'resume_ranker', agentName: 'Search and Match', tokens: 18_500, percent: 27.4 },
          { agentCode: 'email_agent', agentName: 'Email Agent', tokens: 8_000, percent: 11.9 },
          { agentCode: 'submission_agent', agentName: 'Submission Agent', tokens: 6_000, percent: 8.9 },
          { agentCode: 'pipeline_agent', agentName: 'Pipeline Agent', tokens: 4_500, percent: 6.7 },
          { agentCode: 'notes_agent', agentName: 'Notes Agent', tokens: 3_000, percent: 4.4 },
          { agentCode: 'task_manager', agentName: 'Task Manager', tokens: 2_500, percent: 3.7 },
          {
            agentCode: 'interview_scheduling_agent',
            agentName: 'Interview Scheduling',
            tokens: 2_000,
            percent: 3.0,
          },
          { agentCode: 'voice_ai_call_agent', agentName: 'Voice AI', tokens: 1_000, percent: 1.5 },
        ],
      });
    }
    const encId = encodeURIComponent(String(quotaId).trim());
    // Domain Orchestrator company DB — not BigQuery.
    return this.api
      .get<Record<string, unknown>>(this.path(`token-allocation/user-quotas/${encId}/usage-metrics`), {
        period,
      })
      .pipe(map((res) => normalizeUserQuotaUsageMetrics(res)));
  }

  getUsageSummary(query: {
    period?: UserQuotaUsagePeriod;
    userIds?: number[] | null;
    agentCodes?: string[] | null;
  } = {}): Observable<UsageSummary> {
    if (this.useMock()) {
      const period = query.period ?? 'this_month';
      const mockMetrics = {
        dataSource: 'company_database' as const,
        period,
        dateFrom: new Date(Date.now() - 30 * 86400000).toISOString(),
        dateTo: new Date().toISOString(),
        totalTokens: 397_000,
        byAgent: [
          { agentCode: 'ask_anything', agentName: 'Ask Anything', tokens: 185_000, percent: 46.6 },
          { agentCode: 'resume_ranker', agentName: 'Search and Match', tokens: 84_000, percent: 21.2 },
          { agentCode: 'email_agent', agentName: 'Email Agent', tokens: 42_000, percent: 10.6 },
          { agentCode: 'submission_agent', agentName: 'Submission Agent', tokens: 36_000, percent: 9.1 },
          { agentCode: 'pipeline_agent', agentName: 'Pipeline Agent', tokens: 24_000, percent: 6.0 },
          { agentCode: 'interview_scheduling_agent', agentName: 'Interview Scheduling', tokens: 12_000, percent: 3.0 },
          { agentCode: 'voice_ai_call_agent', agentName: 'Voice AI', tokens: 8_000, percent: 2.0 },
          { agentCode: 'task_manager', agentName: 'Task Manager', tokens: 4_000, percent: 1.0 },
          { agentCode: 'notes_agent', agentName: 'Notes Agent', tokens: 2_000, percent: 0.5 },
        ],
        dailyByAgent: [
          'Aug 21', 'Aug 22', 'Aug 23', 'Aug 24', 'Aug 25', 'Aug 26', 'Aug 27',
        ].map((label, i) => ({
          date: `2026-08-${21 + i}`,
          totalTokens: 42000 + i * 4000,
          byAgent: {
            ask_anything: 12000 + i * 500,
            resume_ranker: 9000 + i * 300,
            email_agent: 5000,
            submission_agent: 4000,
            pipeline_agent: 3000,
            notes_agent: 1000,
            task_manager: 800,
            interview_scheduling_agent: 700,
            voice_ai_call_agent: 500,
          },
        })),
        topUsers: [
          {
            userId: 1,
            userName: 'Veerendra',
            userEmail: 'veerendra.b@ceipal.com',
            totalTokens: 128_000,
            topAgentCode: 'ask_anything',
            topAgentName: 'Ask Anything',
            topAgentTokens: 62_000,
          },
          {
            userId: 2,
            userName: 'Hima',
            userEmail: 'varsha.p@ceipal.com',
            totalTokens: 96_000,
            topAgentCode: 'resume_ranker',
            topAgentName: 'Search and Match',
            topAgentTokens: 41_000,
          },
        ],
        filterUsers: [],
        scopeUserId: null,
        agentFilter:
          (query.agentCodes ?? []).map((code) => code.trim()).filter(Boolean).join(',') || null,
        isAdministrator: true,
      };
      return of(normalizeUsageSummary(mockMetrics));
    }
    const params: Record<string, string> = {};
    if (query.period) {
      params['period'] = query.period;
    }
    const userIds = (query.userIds ?? [])
      .map((id) => Math.trunc(Number(id)))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (userIds.length) {
      params['userIds'] = userIds.join(',');
    }
    const agentCodes = (query.agentCodes ?? []).map((code) => code.trim()).filter(Boolean);
    if (agentCodes.length) {
      params['agentCodes'] = agentCodes.join(',');
    }
    return this.api
      .get<Record<string, unknown>>(this.path('token-allocation/usage-summary'), params)
      .pipe(map((res) => normalizeUsageSummary(res)));
  }

  listTokenAllocationActivities(
    query: ListTokenAllocationActivitiesQuery = {},
  ): Observable<TokenAllocationActivityListResponse> {
    if (this.useMock()) {
      const items = [
        {
          id: 'mock-act-1',
          description: 'Assigned 1,000 AI tokens to Jane Recruiter (jane@example.com)',
          createdAt: new Date().toISOString(),
        },
      ];
      return of({ items, total: items.length, page: 1, limit: 20, hasMore: false });
    }
    const params: Record<string, string | number> = {};
    if (query.page != null) params['page'] = query.page;
    if (query.limit != null) params['limit'] = query.limit;
    if (query.q?.trim()) params['q'] = query.q.trim();
    return this.api
      .get<Record<string, unknown>>(this.path('token-allocation/activities'), params)
      .pipe(
        map((res) => {
          const rawItems = Array.isArray(res['items']) ? (res['items'] as Record<string, unknown>[]) : [];
          return {
            items: rawItems.map((row) => normalizeTokenAllocationActivity(row)),
            total: Number(res['total'] ?? rawItems.length) || 0,
            page: Number(res['page'] ?? query.page ?? 1) || 1,
            limit: Number(res['limit'] ?? query.limit ?? 20) || 20,
            hasMore: Boolean(res['hasMore'] ?? res['has_more']),
          };
        }),
      );
  }
}

// --- In-memory mock store (only when agenticAiUseMock is true) ---

let mockIdSeq = 1000;
function mockNextId(): string {
  mockIdSeq += 1;
  return `mock-enc-id-${mockIdSeq}`;
}

const mockConversations = new Map<string, AgenticAiConversation>();
const mockMessages = new Map<string, AgenticAiMessage[]>();

function mockStoreConversation(c: AgenticAiConversation): void {
  mockConversations.set(c.id, c);
}

function mockGetConversation(id: string): AgenticAiConversation | undefined {
  return mockConversations.get(id);
}

function mockConversationList(offset = 0, limit = 5): AgenticAiConversationListResponse {
  const all = [...mockConversations.values()].sort((a, b) =>
    String(b.id).localeCompare(String(a.id)),
  );
  const items = all.slice(offset, offset + limit);
  const total = all.length;
  const hasMore = offset + items.length < total;
  return { items, total, hasMore };
}

function mockMessagesPage(conversationId: string, _query: GetMessagesQuery): PaginatedMessagesResponse {
  const items = [...(mockMessages.get(conversationId) ?? [])];
  return { items, nextCursor: null, hasMore: false };
}

function mockPostMessage(conversationId: string, body: PostUserMessageDto): PostUserMessageResponse {
  const list = mockMessages.get(conversationId) ?? [];
  const maxSeq = list.reduce((m, x) => Math.max(m, x.sequence), 0);
  const userMessage: AgenticAiMessage = {
    id: mockNextId(),
    conversationId,
    sequence: maxSeq + 1,
    role: 'user',
    content: body.content,
    createdAt: new Date().toISOString(),
  };
  const assistantMessage: AgenticAiMessage = {
    id: mockNextId(),
    conversationId,
    sequence: maxSeq + 2,
    role: 'assistant',
    content: 'Mock assistant: received your message. Connect the Nest API to get real replies.',
    model: 'mock',
    metadata: { source: 'mock' },
    createdAt: new Date().toISOString(),
  };
  mockMessages.set(conversationId, [...list, userMessage, assistantMessage]);
  const conv = mockConversations.get(conversationId);
  if (conv) {
    conv.updatedAt = new Date().toISOString();
    if (!conv.title || conv.title === 'New chat') {
      conv.title = body.content.slice(0, 48) + (body.content.length > 48 ? '…' : '');
    }
  }
  return { userMessage, assistantMessage };
}
